import { useCallback, useEffect, useMemo, useState } from 'react'

import { Dropdown, Menu } from 'antd'
import { isArray, isEmpty, xor } from 'lodash'
import { useSelector } from 'react-redux'
import { faFileExport, faList } from '@fortawesome/pro-solid-svg-icons'

import Analytics from '../../../utils/analytics'
import StudentsTable, { getStudentActiveKeys } from './StudentsTable'
import { DEFAULT_STUDENT_FILTERS } from './StudentFilters'
import { selectUser } from '../../../reducers/UserReducer'
import { StudentProperties } from '../../../utils/entities/student'
import { OrderDirections } from '../../../utils/constants'
import { useLocalStorage } from '../../../hooks/UseLocalStorage'
import { retrieveMetadata } from '../../../utils/http'
import { exportStudents, getAllStudents, SerializationContexts as _SC } from '../../../utils/api/student'
import LOCAL_STORAGE_KEYS from '../../../utils/localStorage'
import { TableParameters } from '../../../utils/entities/tableParameters'
import StudentActionsProvider from '../../../Providers/Student/StudentActions'
import DefaultContainer from '../../../HOC/DefaultContainer'
import IconLabel from '../../../Components/icons/IconLabel'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import TooltipIconButton from '../../../Components/antd/Buttons/TooltipIconButton'
import { onError, onInfo } from '../../../utils/apiHelper'
import { selectActiveLanguageCode } from '../../../reducers/LanguageReducer'
import { getDocument } from '../../../utils/api/export'
import StudentFormModal from '../../../Providers/Forms/StudentForm/StudentFormModal'
import { Actions } from '../../../utils/events'

const { EMAIL } = StudentProperties
const STUDENTS_CONTEXTS = [_SC.NISS, _SC.BADGE_ASSIGNATIONS]

/**
 * The hidden columns are not persisted : every reload displays all the columns available to the user. Realign the
 * persisted ones, otherwise a column added since the last visit stays out of the search scope and of the export.
 * Compared as sets : the persisted order is the one of the table columns, which is not this function's to decide.
 */
const withAvailableColumns = (parameters, user) => {
  const columns = getStudentActiveKeys(user)

  return isEmpty(xor(parameters?.filters?.columns, columns))
    ? parameters
    : new TableParameters({ ...parameters, filters: { ...parameters?.filters, columns } })
}

const StudentsView = () => {
  const currentLanguage = useSelector(selectActiveLanguageCode)
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [students, setStudents] = useState({ data: [], loading: false, metadata: null })
  const [parameters, setParameters] = useLocalStorage(
    LOCAL_STORAGE_KEYS.STUDENTS_VIEW.LIST_PARAMETERS,
    new TableParameters({
      order: { by: EMAIL, direction: OrderDirections.ASC },
      pageSize: 50,
      filters: { ...DEFAULT_STUDENT_FILTERS, columns: getStudentActiveKeys(user) }
    }),
    parameters => withAvailableColumns(parameters, user)
  )

  const parametersWithMeta = useMemo(() => new TableParameters({ ...parameters, metadata: students.metadata }), [parameters, students.metadata])

  const fetchStudents = useCallback(parameters => {
    setStudents(prev => ({ ...prev, loading: true }))

    getAllStudents(user, { ...parameters.toQueryParameters(), contexts: STUDENTS_CONTEXTS }).then(({ data, meta }) => {
      setStudents({ data, loading: false, metadata: retrieveMetadata(meta) })
    })
  }, [user?.id])

  useEffect(() => Analytics.pageView('/institution-students'), [])
  useEffect(() => {
    fetchStudents(new TableParameters({ ...parameters }))
  }, [fetchStudents, parameters])

  const handleStudentsExport = () => {
    onInfo(t('common.start_export'))
    exportStudents(user, {
      order: parameters.order,
      language: currentLanguage,
      filters: { ...parameters.filters, columns: ['id', ...(isArray(parameters.filters.columns) ? parameters.filters.columns : [])] }
    }).then(({ job }) => getDocument(job, user, () => onError(t('spreadsheet_download.error'))
    )).catch(error => {
      console.error(error.message)
      onError(t('internships_export.error'))
    })
  }

  const exporter = () => (
    <Dropdown
      trigger={['click']}
      overlay={
        <Menu>
          <Menu.Item onClick={handleStudentsExport}>
            <IconLabel icon={faList} text={t('students_view.export.list')} />
          </Menu.Item>
        </Menu>
      }
    >
      <TooltipIconButton icon={faFileExport} title={t('Export')} />
    </Dropdown>
  )

  // `columns` and `search` live in the same bag but are not filters: only the menu's own keys are reset.
  const handleResetFilters = useCallback(() => setParameters(prev => new TableParameters({
    ...prev, filters: { ...prev.filters, ...DEFAULT_STUDENT_FILTERS }, page: 1
  })), [setParameters])

  const handleStudentChange = useCallback((updatedStudent, action) => {
    if (action === Actions.UPDATE) {
      setStudents(prev => ({
        ...prev,
        data: prev.data.map(s => s.id === updatedStudent.id ? updatedStudent : s)
      }))
    }
  }, [])

  return (
    <DefaultContainer>
      <StudentActionsProvider>
        <StudentFormModal>
          <StudentsTable
            data={students.data}
            exporter={exporter}
            loading={students.loading}
            parameters={parametersWithMeta}
            onChange={handleStudentChange}
            onFiltersChange={filters => setParameters(prev => new TableParameters({ ...prev, filters, page: 1 }))}
            onKeysChanged={keys => setParameters(prev => new TableParameters({ ...prev, filters: { ...prev.filters, columns: keys }, page: 1 }))}
            onParametersChange={parameters => setParameters(prev => new TableParameters({
              ...prev, ...parameters, page: parameters.search !== prev.search ? 1 : parameters.page
            }))}
            onResetFilters={handleResetFilters}
          />
        </StudentFormModal>
      </StudentActionsProvider>
    </DefaultContainer>
  )
}

export default StudentsView
